import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Input } from '@agms/ui'
import { applicationApi, type Application } from '../../../lib/api'
import { apiErrorMessage } from '../../../hooks/useErrorHandler'
import { SectionBody, SectionHeader } from './StageContextLayout'

export function EsignSentContext({ app }: { app?: Application | null }) {
  const queryClient = useQueryClient()
  const [editingEmail, setEditingEmail] = useState(false)
  const [emailDraft, setEmailDraft] = useState(app?.owner_email ?? '')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)

  // Single mutation covers both "resend same address" (no arg) and
  // "change email + resend" (pass new address). Backend handles
  // the email swap, void, and re-dispatch in one transaction.
  const resend = useMutation({
    mutationFn: async (newEmail?: string) => {
      if (!app) throw new Error('No application')
      return applicationApi.resendSignature(app.id, newEmail)
    },
    onSuccess: (_data, variables) => {
      setActionError(null)
      setEditingEmail(false)
      setActionSuccess(
        variables
          ? `Signer email updated to ${variables} and a new envelope is on its way.`
          : 'A new envelope is on its way to the current signer.',
      )
      // Refresh the lead query so the updated owner_email +
      // boldsign_* columns show up in the card without a page reload.
      if (app?.lead_id) {
        queryClient.invalidateQueries({ queryKey: ['lead', app.lead_id] })
      }
    },
    onError: (error) => {
      setActionSuccess(null)
      setActionError(apiErrorMessage(error, 'Resend failed. Check the pipeline history for details.'))
    },
  })

  const handleResendSameEmail = () => {
    setActionError(null)
    setActionSuccess(null)
    resend.mutate(undefined)
  }

  const handleStartEmailEdit = () => {
    setEmailDraft(app?.owner_email ?? '')
    setEmailError(null)
    setActionError(null)
    setActionSuccess(null)
    setEditingEmail(true)
  }

  const handleSubmitEmailChange = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = emailDraft.trim()
    // Same validator Laravel uses client-side for fast feedback.
    // Server-side validation is the real gate.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError('Please enter a valid email address.')
      return
    }
    if (trimmed === app?.owner_email) {
      setEmailError('Email is unchanged — use Resend instead.')
      return
    }
    setEmailError(null)
    resend.mutate(trimmed)
  }

  return (
    <>
      <SectionHeader color="#dbeafe">
        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#1e40af' }}>Pending Signature</h3>
      </SectionHeader>
      <SectionBody>
        <dl style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <dt style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>BoldSign Document</dt>
            <dd style={{ fontSize: '14px', color: '#111827', fontFamily: 'monospace' }}>
              {app?.boldsign_document_id ? app.boldsign_document_id.slice(0, 12) + '…' : '—'}
            </dd>
          </div>
          <div>
            <dt style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>Sent At</dt>
            <dd style={{ fontSize: '14px', color: '#111827' }}>
              {app?.boldsign_sent_at ? new Date(app.boldsign_sent_at).toLocaleString() : '—'}
            </dd>
          </div>
          <div>
            <dt style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>Signer</dt>
            <dd style={{ fontSize: '14px', color: '#111827' }}>
              {[app?.owner_first_name, app?.owner_last_name].filter(Boolean).join(' ') || '—'}
            </dd>
          </div>
          <div>
            <dt style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>Signer Email</dt>
            <dd style={{ fontSize: '14px', color: '#111827', wordBreak: 'break-all' }}>
              {app?.owner_email || '—'}
            </dd>
          </div>
        </dl>

        {/* Actions: either the resting "Resend" / "Change Email &
            Resend" buttons, or the inline edit form when the user
            clicks "Change Email & Resend". */}
        {!editingEmail && app?.id && (
          <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              onClick={handleResendSameEmail}
              isLoading={resend.isPending && !editingEmail}
              disabled={resend.isPending}
            >
              Resend
            </Button>
            <Button
              variant="outline"
              onClick={handleStartEmailEdit}
              disabled={resend.isPending}
            >
              Change Email &amp; Resend
            </Button>
          </div>
        )}

        {editingEmail && (
          <form onSubmit={handleSubmitEmailChange} style={{ marginTop: '16px' }}>
            <Input
              label="New signer email"
              type="email"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.currentTarget.value)}
              error={emailError ?? undefined}
              helperText="The current envelope will be voided and a fresh one sent to this address."
              autoFocus
              required
            />
            <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
              <Button type="submit" isLoading={resend.isPending}>
                Save &amp; Resend
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setEditingEmail(false)
                  setEmailError(null)
                }}
                disabled={resend.isPending}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Result banners — visible after a completed action. */}
        {actionError && (
          <div
            style={{
              marginTop: '12px',
              padding: '8px 12px',
              borderRadius: '6px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#991b1b',
              fontSize: '13px',
              lineHeight: 1.5,
            }}
          >
            {actionError}
          </div>
        )}
        {actionSuccess && !actionError && (
          <div
            style={{
              marginTop: '12px',
              padding: '8px 12px',
              borderRadius: '6px',
              backgroundColor: '#ecfdf5',
              border: '1px solid #a7f3d0',
              color: '#065f46',
              fontSize: '13px',
              lineHeight: 1.5,
            }}
          >
            {actionSuccess}
          </div>
        )}
      </SectionBody>
    </>
  )
}
